import React, { useEffect, useMemo, useState } from 'react';
import {
  Drawer, Box, Typography, Tabs, Tab, IconButton, Alert, Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LockIcon from '@mui/icons-material/Lock';
import { toast } from 'react-toastify';
import StatusChip from '../../../components/StatusChip';
import InvButton from '../../../components/InvButton';
import SplitPaymentCollector from '../../../components/SplitPaymentCollector';
import ReceiptView from '../../../components/ReceiptView';
import { InvTextField } from '../../../components/InvField';
import {
  getBill,
  getBillPayment,
  listBillRefunds,
  raiseRefund,
  actionRefund,
  settleRefund,
  submitBillPayment,
  recheckBill,
  finalizeBill,
} from '../../../api/pharmacyDispense';
import { resolvePharmacyScope, getPharmacyActorId } from '../../../hooks/usePharmacyRole';
import { toInventoryList } from '../../../api/inventory-request';
import { formatINR, formatDate, formatDateTime, parseMoney } from '../../../utils';
import {
  mapList,
  mapPharmacyRefund,
  pharmacyRefundCnNo,
  pharmacyRefundRemaining,
  pharmacyRefundItemLabel,
} from '../../../utils/pharmacyMappers';
import { IPD_COLORS } from '../../../theme';

function TabPanel({ value, index, children }) {
  if (value !== index) return null;
  return <Box sx={{ pt: 2 }}>{children}</Box>;
}

function remainingRefundable(line) {
  const refundable = parseMoney(line.refundable_qty ?? line.quantity ?? line.qty);
  const refunded = parseMoney(line.refunded_qty);
  const pending = parseMoney(line.pending_refund_qty);
  return Math.max(0, refundable - refunded - pending);
}

function refundCnNo(row) {
  return pharmacyRefundCnNo(row);
}

function refundSlNo(row) {
  return row?.sale_number
    || row?.sale_no
    || row?.sl_number
    || row?.sl_no
    || row?.dispense_number
    || row?.sale_id
    || '—';
}

function RefundItemsBlock({ row }) {
  const items = row?.refund_items || row?.items || [];
  if (!items.length) return null;
  return (
    <Box sx={{ mt: 1, mb: 0.5 }}>
      {items.map((item, i) => (
        <Box
          key={item.bill_line_id || item.item_id || i}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: 1,
            py: 0.4,
            borderBottom: i < items.length - 1 ? `1px dashed ${IPD_COLORS.border}` : 'none',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1.3 }}>
              {item.item_name || pharmacyRefundItemLabel(item)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
              Qty {item.return_quantity ?? item.quantity ?? '—'}
              {item.batch_number ? ` · Batch ${item.batch_number}` : ''}
            </Typography>
          </Box>
          {item.refund_amount != null && item.refund_amount !== '' && (
            <Typography sx={{ fontSize: 12, fontWeight: 600, flexShrink: 0 }}>
              {formatINR(item.refund_amount)}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
}

function extractDoctorFromRemarks(remarks) {
  if (!remarks) return '';
  const match = String(remarks).match(/Doctor:\s*([^·|]+)/i);
  return match ? match[1].trim() : '';
}

/** Resolve medicine/item display name from varied bill-line API shapes. */
function billLineItemName(line) {
  if (!line || typeof line !== 'object') return '';
  const nested = line.item || line.medicine || line.product || line.sku || line.item_master || {};
  const candidates = [
    line.item_name,
    line.medicine_name,
    line.product_name,
    line.drug_name,
    line.brand_name,
    line.generic_name,
    line.display_name,
    line.description,
    line.item_description,
    line.material_name,
    line.name,
    nested.item_name,
    nested.medicine_name,
    nested.product_name,
    nested.brand_name,
    nested.generic_name,
    nested.display_name,
    nested.name,
    typeof line.item === 'string' ? line.item : null,
    typeof line.medicine === 'string' ? line.medicine : null,
  ];
  const found = candidates.find((v) => v != null && String(v).trim() !== '');
  return found ? String(found).trim() : '';
}

export default function BillDetailDrawer({ open, billId, store, onClose, onChanged, initialTab = 0 }) {
  const [tab, setTab] = useState(0);
  const [bill, setBill] = useState(null);
  const [payment, setPayment] = useState(undefined); // undefined=loading, null=none
  const [refunds, setRefunds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [raiseOpen, setRaiseOpen] = useState(false);
  const [returnQtys, setReturnQtys] = useState({});
  const [refundReason, setRefundReason] = useState('');
  const [busy, setBusy] = useState(false);

  const [settleTarget, setSettleTarget] = useState(null);
  const [approveTarget, setApproveTarget] = useState(null);
  const [approveAmount, setApproveAmount] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const scope = useMemo(() => resolvePharmacyScope(store, bill || {}), [store, bill]);
  const lines = toInventoryList(
    bill?.lines
    || bill?.bill_lines
    || (Array.isArray(bill?.items) && bill.items[0] && (bill.items[0].quantity != null || bill.items[0].unit_price != null)
      ? bill.items
      : []),
  );

  async function reload() {
    if (!billId) return;
    setLoading(true);
    setError(null);
    try {
      const [b, p, r] = await Promise.all([
        getBill(billId, scope),
        getBillPayment(billId, scope),
        listBillRefunds(billId, scope),
      ]);
      setBill(b);
      setPayment(p);
      setRefunds(mapList(r, mapPharmacyRefund));
    } catch (err) {
      setError(err?.message || 'Failed to load bill');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open && billId) {
      setTab(initialTab || 0);
      reload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, billId, initialTab]);

  const status = bill?.bill_status || bill?.status;
  const patientLabel = bill?.search_name
    || bill?.patient_name
    || (String(bill?.patient_id || '').toUpperCase().startsWith('WALKIN') ? 'Walk-in' : '')
    || 'Walk-in';
  const amountPayable = bill?.amount_payable;

  const receiptLineDiscount = lines.reduce(
    (sum, line) => sum + parseMoney(line.item_discount_amount || line.discount_amount || line.line_discount),
    0,
  );
  const receiptBillDiscount = lines.reduce(
    (sum, line) => sum + parseMoney(line.bill_discount_share_amount),
    0,
  ) || parseMoney(bill?.bill_discount_amount || bill?.discount_amount);

  async function handleCollect(modes) {
    setBusy(true);
    try {
      const st = status;
      if (st !== 'FINALIZED' && st !== 'POSTED' && st !== 'PAID') {
        await recheckBill(billId, {}, scope);
        await finalizeBill(billId, {}, scope);
      }
      await submitBillPayment(billId, { modes }, scope);
      toast.success('Payment collected');
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || 'Payment failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleRaiseRefund() {
    const items = lines
      .map((line) => {
        const id = line.bill_line_id || line.id;
        const qty = Number(returnQtys[id] || 0);
        if (qty <= 0) return null;
        return { bill_line_id: id, return_quantity: String(qty) };
      })
      .filter(Boolean);

    if (!items.length || !refundReason.trim()) {
      toast.error('Select return qty and enter a reason');
      return;
    }
    setBusy(true);
    try {
      const result = await raiseRefund(billId, { items, reason: refundReason.trim() }, scope);
      const cnLabel = pharmacyRefundCnNo(result);
      toast.success(`Credit note ${cnLabel !== '—' ? cnLabel : (result?.refund_number || '')} raised`);
      setRaiseOpen(false);
      setReturnQtys({});
      setRefundReason('');
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || 'Refund failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleApprove() {
    if (!approveTarget) return;
    const requested = parseMoney(approveTarget.total_amount || approveTarget.amount);
    const approved = parseMoney(approveAmount);
    if (approved <= 0 || approved > requested + 0.0001) {
      toast.error('Approved amount must be > 0 and ≤ requested');
      return;
    }
    setBusy(true);
    try {
      await actionRefund(billId, approveTarget.id || approveTarget.refund_id, {
        action: 'approve',
        actioned_by: getPharmacyActorId(),
        approved_amount: String(approved.toFixed(4)),
      }, scope);
      toast.success('Refund approved');
      setApproveTarget(null);
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || 'Approve failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleReject() {
    if (!rejectTarget || !rejectReason.trim()) {
      toast.error('Rejection reason is required');
      return;
    }
    setBusy(true);
    try {
      await actionRefund(billId, rejectTarget.id || rejectTarget.refund_id, {
        action: 'reject',
        actioned_by: getPharmacyActorId(),
        rejection_reason: rejectReason.trim(),
      }, scope);
      toast.success('Refund rejected');
      setRejectTarget(null);
      setRejectReason('');
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || 'Reject failed');
    } finally {
      setBusy(false);
    }
  }

  async function handleSettle(modes) {
    if (!settleTarget) return;
    setBusy(true);
    try {
      await settleRefund(billId, settleTarget.id || settleTarget.refund_id, {
        modes,
        remarks: 'Refund settlement',
      }, scope);
      toast.success('Refund settled — stock return posted to ledger');
      setSettleTarget(null);
      await reload();
      onChanged?.();
    } catch (err) {
      toast.error(err?.message || 'Settle failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 520 } } }}>
      <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 18 }}>Bill detail</Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
              {billId} · {patientLabel}
            </Typography>
          </Box>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Box>

        {bill && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
            <StatusChip status={status} />
            <Typography sx={{ fontWeight: 700 }}>{formatINR(amountPayable)}</Typography>
          </Box>
        )}

        {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
        {loading && <Typography sx={{ fontSize: 13 }}>Loading…</Typography>}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: `1px solid ${IPD_COLORS.border}` }}>
          <Tab label="Lines" />
          <Tab label="Payment" />
          <Tab label="Receipt" />
          <Tab label="Refunds" />
        </Tabs>

        <Box sx={{ flex: 1, overflow: 'auto' }}>
          <TabPanel value={tab} index={0}>
            {lines.map((line) => {
              const controlled = line.is_controlled_drug || line.controlled;
              return (
                <Box key={line.bill_line_id || line.id} sx={{
                  mb: 1.25, p: 1.25, borderRadius: '8px', border: `1px solid ${IPD_COLORS.border}`,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                      {billLineItemName(line) || 'Item'}
                      {controlled && <LockIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />}
                    </Typography>
                    <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                      {formatINR(line.line_net_amount || line.net_amount || line.total)}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                    Batch {line.batch_number || '—'}
                    {line.expiry_date ? ` · Exp ${formatDate(line.expiry_date)}` : ''}
                    {' · '}Qty {line.quantity || line.qty}
                    {(line.unit_price || line.unit_rate) ? ` · ${formatINR(line.unit_price || line.unit_rate)}` : ''}
                  </Typography>
                  {(parseMoney(line.item_discount_amount) > 0 || parseMoney(line.bill_discount_share_amount) > 0) && (
                    <Typography sx={{ fontSize: 11, color: IPD_COLORS.success.text }}>
                      {parseMoney(line.item_discount_amount) > 0
                        ? `Discount − ${formatINR(line.item_discount_amount)}`
                        : ''}
                      {parseMoney(line.item_discount_amount) > 0 && parseMoney(line.bill_discount_share_amount) > 0
                        ? ' · '
                        : ''}
                      {parseMoney(line.bill_discount_share_amount) > 0
                        ? `Bill share − ${formatINR(line.bill_discount_share_amount)}`
                        : ''}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                    CGST {line.cgst_amount || '0'} · SGST {line.sgst_amount || '0'} · IGST {line.igst_amount || '0'}
                  </Typography>
                  <Typography sx={{ fontSize: 11 }}>
                    Refundable {remainingRefundable(line)} / Refunded {parseMoney(line.refunded_qty)}
                  </Typography>
                </Box>
              );
            })}
            {!lines.length && !loading && (
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>No lines</Typography>
            )}
          </TabPanel>

          <TabPanel value={tab} index={1}>
            {payment === null && (
              <Box>
                <Typography sx={{ fontSize: 13, mb: 2, color: IPD_COLORS.textSecondary }}>
                  No payment collected yet
                </Typography>
                <Tooltip title={status === 'FINALIZED' || status === 'POSTED' ? '' : 'Bill must be finalized first'}>
                  <span>
                    <SplitPaymentCollector
                      totalDue={amountPayable}
                      submitting={busy}
                      disabled={!(status === 'FINALIZED' || status === 'POSTED')}
                      onSubmit={handleCollect}
                    />
                  </span>
                </Tooltip>
              </Box>
            )}
            {payment && (
              <Box>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  {payment.receipt_number || payment.receipt_no}
                </Typography>
                <StatusChip status={payment.status || payment.payment_status || 'SUCCESS'} />
                <Typography sx={{ fontSize: 13, mt: 1 }}>
                  Total {formatINR(payment.total_amount || payment.amount || amountPayable)}
                </Typography>
                <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary }}>
                  Cashier {payment.cashier_name || '—'} · {formatDateTime(payment.created_at)}
                </Typography>
                {toInventoryList(payment.modes || payment.payment_modes).map((m, i) => (
                  <Typography key={i} sx={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                    <span>{m.mode}{m.txn_ref ? ` (${m.txn_ref})` : ''}</span>
                    <span>{formatINR(m.amount)}</span>
                  </Typography>
                ))}
                <InvButton
                  size="small"
                  variant="outlined"
                  tone="teal"
                  sx={{ mt: 2 }}
                  onClick={() => setTab(2)}
                >
                  View full receipt
                </InvButton>
              </Box>
            )}
          </TabPanel>

          <TabPanel value={tab} index={2}>
            {!payment && payment !== undefined && (
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>
                Receipt is available after payment is collected. Use this view to verify items and expiry before approving a refund.
              </Typography>
            )}
            {payment && (
              <ReceiptView
                receiptNumber={payment.receipt_number || payment.receipt_no || payment.id}
                billNumber={bill?.bill_number || billId}
                patientLabel={patientLabel}
                doctorName={bill?.doctor_name || bill?.prescriber_name || extractDoctorFromRemarks(bill?.remarks)}
                lines={lines.map((line) => ({
                  id: line.id || line.bill_line_id,
                  name: billLineItemName(line),
                  batch_number: line.batch_number,
                  expiry_date: line.expiry_date || line.expiry,
                  qty: line.quantity || line.qty || line.billed_qty,
                  rate: line.unit_price || line.unit_rate || line.rate || line.sale_price || line.mrp,
                  line_basic_amount: line.line_basic_amount,
                  item_discount_amount: line.item_discount_amount,
                  bill_discount_share_amount: line.bill_discount_share_amount,
                  line_net_amount: line.line_net_amount || line.net_amount || line.total || line.line_amount,
                }))}
                totalBasic={bill?.total_basic}
                lineDiscountTotal={
                  receiptLineDiscount > 0
                    ? receiptLineDiscount
                    : Math.max(0, parseMoney(bill?.total_discount) - receiptBillDiscount)
                }
                billDiscount={receiptBillDiscount}
                tax={bill?.total_tax}
                amountPayable={payment.total_amount || payment.amount || amountPayable || bill?.total_net}
                modes={toInventoryList(payment.modes || payment.payment_modes)}
                cashierName={payment.cashier_name}
                createdAt={payment.created_at || bill?.updated_at || bill?.created_at}
              />
            )}
          </TabPanel>

          <TabPanel value={tab} index={3}>
            <Box sx={{ mb: 2 }}>
              <Tooltip title={status === 'PAID' ? '' : 'Refunds only allowed on PAID bills'}>
                <span>
                  <InvButton
                    size="small"
                    disabled={status !== 'PAID'}
                    onClick={() => setRaiseOpen(true)}
                  >
                    Raise refund
                  </InvButton>
                </span>
              </Tooltip>
            </Box>

            {raiseOpen && (
              <Box sx={{ mb: 2, p: 1.5, border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px' }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>Raise refund</Typography>
                {lines.map((line) => {
                  const id = line.bill_line_id || line.id;
                  const max = remainingRefundable(line);
                  const controlled = line.is_controlled_drug || line.controlled;
                  return (
                    <Box key={id} sx={{ mb: 1 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                        {billLineItemName(line) || line.item_name || line.name || 'Item'}
                        {controlled ? ' — not returnable (controlled drug)' : ` (max ${max})`}
                      </Typography>
                      <InvTextField
                        type="number"
                        label="Return qty"
                        value={returnQtys[id] || ''}
                        disabled={controlled || max <= 0 || busy}
                        inputProps={{ min: 0, max }}
                        onChange={(e) => setReturnQtys((q) => ({ ...q, [id]: e.target.value }))}
                      />
                    </Box>
                  );
                })}
                <InvTextField
                  label="Reason *"
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  disabled={busy}
                />
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <InvButton loading={busy} onClick={handleRaiseRefund}>Submit</InvButton>
                  <InvButton variant="outlined" tone="teal" disabled={busy} onClick={() => setRaiseOpen(false)}>
                    Cancel
                  </InvButton>
                </Box>
              </Box>
            )}

            {refunds.map((rf) => {
              const id = rf.id || rf.refund_id;
              const st = rf.status;
              return (
                <Box key={id} sx={{
                  mb: 1.25, p: 1.25, borderRadius: '8px', border: `1px solid ${IPD_COLORS.border}`,
                }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, fontSize: 13 }}>
                        CN {refundCnNo(rf)}
                      </Typography>
                      <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                        SL {refundSlNo(rf)}
                      </Typography>
                    </Box>
                    <StatusChip status={st} />
                  </Box>
                  <Typography sx={{ fontSize: 12 }}>
                    {formatINR(rf.total_amount || rf.amount)} · {rf.reason || '—'}
                  </Typography>
                  <RefundItemsBlock row={rf} />
                  {rf.rejection_reason && (
                    <Typography sx={{ fontSize: 11, color: IPD_COLORS.error.text, mt: 0.5 }}>
                      Rejected: {rf.rejection_reason}
                    </Typography>
                  )}
                  <Typography sx={{ fontSize: 11, color: IPD_COLORS.textSecondary }}>
                    Remaining {formatINR(pharmacyRefundRemaining(rf))}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                    {st === 'PENDING' && (
                      <>
                        <InvButton
                          size="small"
                          disabled={busy}
                          onClick={() => {
                            setApproveTarget(rf);
                            setApproveAmount(String(parseMoney(rf.total_amount || rf.amount)));
                          }}
                        >
                          Approve
                        </InvButton>
                        <InvButton
                          size="small"
                          variant="outlined"
                          tone="teal"
                          disabled={busy}
                          onClick={() => setRejectTarget(rf)}
                        >
                          Reject
                        </InvButton>
                      </>
                    )}
                    {st === 'APPROVED' && pharmacyRefundRemaining(rf) > 0 && (
                      <InvButton size="small" disabled={busy} onClick={() => setSettleTarget(rf)}>
                        Settle
                      </InvButton>
                    )}
                    {st === 'SETTLED' && (
                      <Typography sx={{ fontSize: 11, color: IPD_COLORS.success.text }}>
                        Stock return posted to ledger
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
            {!refunds.length && !loading && (
              <Typography sx={{ fontSize: 13, color: IPD_COLORS.textSecondary }}>No refunds</Typography>
            )}

            {settleTarget && (
              <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${IPD_COLORS.border}`, borderRadius: '8px' }}>
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Settle {refundCnNo(settleTarget)}
                </Typography>
                <RefundItemsBlock row={settleTarget} />
                <SplitPaymentCollector
                  totalDue={pharmacyRefundRemaining(settleTarget)}
                  direction="payout"
                  submitting={busy}
                  onSubmit={handleSettle}
                />
                <InvButton variant="outlined" tone="teal" size="small" sx={{ mt: 1 }} onClick={() => setSettleTarget(null)}>
                  Cancel
                </InvButton>
              </Box>
            )}
          </TabPanel>
        </Box>
      </Box>

      {approveTarget && (
        <Box sx={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400,
        }}>
          <Box sx={{ width: 360, p: 2.5, borderRadius: '12px', backgroundColor: '#fff' }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Approve refund</Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mb: 1 }}>
              CN {refundCnNo(approveTarget)}
              {refundSlNo(approveTarget) !== '—' ? ` · SL ${refundSlNo(approveTarget)}` : ''}
            </Typography>
            <RefundItemsBlock row={approveTarget} />
            <InvTextField
              label="Approved amount (≤ requested)"
              type="number"
              value={approveAmount}
              onChange={(e) => setApproveAmount(e.target.value)}
              sx={{ mt: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <InvButton loading={busy} onClick={handleApprove}>Approve</InvButton>
              <InvButton variant="outlined" tone="teal" onClick={() => setApproveTarget(null)}>Cancel</InvButton>
            </Box>
          </Box>
        </Box>
      )}

      {rejectTarget && (
        <Box sx={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.35)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1400,
        }}>
          <Box sx={{ width: 360, p: 2.5, borderRadius: '12px', backgroundColor: '#fff' }}>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>Reject refund</Typography>
            <Typography sx={{ fontSize: 12, color: IPD_COLORS.textSecondary, mb: 1 }}>
              CN {refundCnNo(rejectTarget)}
              {refundSlNo(rejectTarget) !== '—' ? ` · SL ${refundSlNo(rejectTarget)}` : ''}
            </Typography>
            <RefundItemsBlock row={rejectTarget} />
            <InvTextField
              label="Rejection reason *"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              sx={{ mt: 1.5 }}
            />
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              <InvButton loading={busy} onClick={handleReject}>Reject</InvButton>
              <InvButton variant="outlined" tone="teal" onClick={() => setRejectTarget(null)}>Cancel</InvButton>
            </Box>
          </Box>
        </Box>
      )}
    </Drawer>
  );
}
